import type { Cart, CartTags, CreateCartProps } from '@iheartjane/data-access';
import { ApiError, removeCartProduct } from '@iheartjane/data-access';
import {
  cartKeys,
  useAddCartProduct,
  useAddLoyaltyRedemption,
  useCreateCart,
  useDeleteCart,
  useRemoveCartProduct,
  useRemoveLoyaltyRedemption,
  useUpdateCart,
  useUpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { EventNames, trackError } from '@iheartjane/tracking';
import type { PriceId } from '@iheartjane/types';
import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash-es/pick';
import { useMemo } from 'react';

import { trackCartEvent } from '@jane/monolith/customer/cart';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { selectApplication } from '@jane/monolith/customer/redux/application';
import {
  CRM_REDEMPTION_ERROR_MESSAGE,
  applyCrmRedemption,
  attemptToAddToCart,
  checkout,
  deleteCartItem,
  deleteUnavailableProducts,
  maybeChangeStore,
  openCart,
  removeCrmRedemption,
  resetCart,
  selectCart,
  updateCart,
  updateDeliveryAddress,
  updateQueryPromoCode,
  updateReservationMode,
} from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { NotificationsService } from '@jane/monolith/services/notifications';
import { moeAddToBag, track } from '@jane/shared-ecomm/tracking';
import { postAnalyticsEvent } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';
import { getActualUnitPrice, prepareCartProduct } from '@jane/shared/util';

import { getLocalCartUuid, updateLocalCartUuid } from './localCartUuid';

/**
 * The fields a v3 delete needs. Both carts' `unavailable_products` rows satisfy
 * it, so a caller forwards the list it already holds instead of re-deriving ids.
 */
export interface UnavailableProductRef {
  price_id: PriceId;
  product_id: number;
}

/**
 * The write side of the cart facade. Behind `c3UseV3Cart` the nine actions that
 * have a v3 backing call it directly; flag-off — and, for the three actions
 * still awaiting a v3 body, on both paths — each forwards to the corresponding
 * v1 Redux thunk. The facade name is the canonical v3 name, which differs from
 * the legacy export.
 * TODO(carts-v3): re-point the remaining three actions once their backing exists.
 *
 * The v3 bodies deliberately drop the thunks' `bundlePossibilities` dispatches
 * (`setTouchedProduct`): that is a separate slice whose writes stay on Redux
 * until slot 017, per ADR-0003 — thunk-coupled client state migrates at the flip,
 * not incrementally.
 *
 * Two Redux dispatches survive on the v3 path, both from `addProduct`: the
 * store-switch guard (`maybeChangeStore`) and `openCart()`. Both are client UI,
 * not cart server state — `is_open` is dropped by the v1 → v3 mapper and
 * `replacementCart` is slice-only. `openCart` and `replacementCart` re-home in
 * 015c; `maybeChangeStore`'s third dispatch, `openModal`, targets the modal
 * slice and stays — "no Redux on the v3 path" means no *cart* Redux. See §3 of
 * the slot task; exceptions, not oversights.
 */
export interface CartFacadeActions {
  /**
   * The consumer-level add. Flag-off it forwards `attemptToAddToCart`; flag-on
   * it reproduces that thunk's three client concerns here — the store-switch
   * guard, the add-vs-update branch, and the add-to-cart analytics — over the v3
   * mutations, creating the cart inline when there is no local uuid.
   */
  addProduct: (...args: Parameters<typeof attemptToAddToCart>) => void;
  applyLoyalty: (...args: Parameters<typeof applyCrmRedemption>) => void;
  applyPromoCode: (...args: Parameters<typeof updateQueryPromoCode>) => void;
  checkout: (...args: Parameters<typeof checkout>) => void;
  /**
   * Creates an empty cart for the store slice's store. Flag-off a no-op (v1 has
   * no standalone create — see the comment on its body); flag-on it shares
   * `addProduct`'s create body, which is also the only production caller today.
   */
  createCart: () => void;
  deleteUnavailableProducts: (
    products: readonly UnavailableProductRef[]
  ) => void;
  removeLoyalty: (...args: Parameters<typeof removeCrmRedemption>) => void;
  removeProduct: (...args: Parameters<typeof deleteCartItem>) => void;
  resetCart: (...args: Parameters<typeof resetCart>) => void;
  updateDeliveryAddress: (
    ...args: Parameters<typeof updateDeliveryAddress>
  ) => void;
  updateProduct: (...args: Parameters<typeof updateCart>) => void;
  updateReservationMode: (
    ...args: Parameters<typeof updateReservationMode>
  ) => void;
}

export const useCartActions = (): CartFacadeActions => {
  const dispatch = useCustomerDispatch();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // `storeCurrency` and moe's `storeId`/`storeName` are not on the v3 cart
  // (`Cart.store` is `{ id }`), so both paths take them from the store slice
  // exactly as the thunks do.
  const { store } = useCustomerSelector(selectStore);
  // Both v3 write bodies carry `tags` and `j_device_id`, and v1 read them from
  // these two slices — so read the same two, byte-identically. Narrow selectors,
  // not the whole slice: `selectCart` returns the entire cart state, which would
  // re-render every facade consumer on any cart change, flag-off included.
  // A `selectCart` import inside `cartFacade/` is the facade itself, not a
  // migrated consumer, so it joins no ESLint ban. `tags` re-homes to
  // URL/localStorage in 015c.
  const cartTags = useCustomerSelector((state) => selectCart(state).tags) as
    CartTags | undefined;
  const janeDeviceId = useCustomerSelector(
    (state) => selectApplication(state).janeDeviceId
  );
  const queryClient = useQueryClient();

  // `mutate` is the stable half of each result object, so the actions memo can
  // depend on it without re-creating on every render.
  const { mutate: mutateUpdateCart } = useUpdateCart();
  const { mutate: mutateDeleteCart } = useDeleteCart();
  const { mutate: mutateUpdateCartProduct } = useUpdateCartProduct();
  const { mutate: mutateRemoveCartProduct } = useRemoveCartProduct();
  const { mutate: mutateAddLoyalty } = useAddLoyaltyRedemption();
  const { mutate: mutateRemoveLoyalty } = useRemoveLoyaltyRedemption();
  const { mutate: mutateAddCartProduct } = useAddCartProduct();
  // The only point at which the new uuid exists, so the local store is written
  // here rather than at each call site. `useCreateCart` has already cached the
  // cart under `['cart', uuid]` by the time this runs.
  const { mutate: mutateCreateCart } = useCreateCart({
    onSuccess: (cart) => updateLocalCartUuid(appMode, storeId, cart.uuid),
  });

  return useMemo(() => {
    const v1Actions: CartFacadeActions = {
      addProduct: (...args) => dispatch(attemptToAddToCart(...args)),
      updateProduct: (...args) => dispatch(updateCart(...args)),
      removeProduct: (...args) => dispatch(deleteCartItem(...args)),
      applyPromoCode: (...args) => dispatch(updateQueryPromoCode(...args)),
      updateReservationMode: (...args) =>
        dispatch(updateReservationMode(...args)),
      updateDeliveryAddress: (...args) =>
        dispatch(updateDeliveryAddress(...args)),
      applyLoyalty: (...args) => dispatch(applyCrmRedemption(...args)),
      removeLoyalty: (...args) => dispatch(removeCrmRedemption(...args)),
      // Neither the request body nor the reducer reads the payload, so mapping
      // the rows back to ids is byte-equivalent to what the thunk received.
      deleteUnavailableProducts: (products) =>
        dispatch(
          deleteUnavailableProducts(
            products.map((product) => product.product_id)
          )
        ),
      checkout: (...args) => dispatch(checkout(...args)),
      resetCart: (...args) => dispatch(resetCart(...args)),
      // A cart is created implicitly on first add — there is no standalone create
      // thunk. `createCart` exists on the interface because consumers (and the
      // future v3 backing) expect it, but on the v1 Redux path it is a no-op: the
      // real network create happens inside `addProduct`/`addToCart`. It must NOT
      // dispatch `initiateCreateCart` — that reducer sets `isLoading: true` and the
      // only reducer that clears it is `createHeadlessCartSuccess` (the headless
      // thunk's success). Without an immediately-following headless create, that
      // would strand `isLoading`/`isFetching` stuck true.
      createCart: () => undefined,
    };

    if (!useV3Cart) return v1Actions;

    // A write needs the cart at call time, not reactively, so the snapshot comes
    // from the query cache rather than a second `useCart` observer (which would
    // re-render every consumer of this hook). No uuid or no cached cart is v1's
    // `if (!cart.uuid) return`.
    const cartSnapshot = (uuid: string) =>
      queryClient.getQueryData<Cart>(cartKeys.detail(uuid)) ?? null;

    /**
     * Always invalidate — never predict whether the row we just deleted was the
     * last one. `destroy` answers 204 with no body, so the server's
     * `@cart.destroy! if @cart.unfilled?` is unobservable here, and a snapshot
     * taken before the request races any concurrent removal. A destroyed cart's
     * refetch 404s, which `useCartState` turns into a cleared uuid and an empty
     * bag; that one wasted request buys a decision that cannot be raced.
     */
    const settleAfterRemoval = (uuid: string) =>
      queryClient.invalidateQueries({ queryKey: cartKeys.detail(uuid) });

    /**
     * The client cannot use the backend's create-on-add: `cart_products` is
     * nested on the uuid, so an empty one yields `/v3/carts//cart_products` and
     * 404s at the router before the body is read. So a first add creates the cart
     * with its product inline — one request, not create-then-add.
     *
     * `reservation_mode` and `promo_code` are omitted, not defaulted: v1's
     * implicit create sent neither, and the server derives the mode from the
     * store and app mode. Passing one would change the mode of every first add.
     */
    const createV3Cart = (
      products: CreateCartProps['products'],
      newCartStoreId: number | string,
      onCreated?: (cart: Cart) => void
    ) =>
      mutateCreateCart(
        {
          appMode,
          deviceId: janeDeviceId,
          jwt: getJwt(),
          products,
          storeId: newCartStoreId,
          tags: cartTags,
        },
        { onSuccess: onCreated }
      );

    const updateProduct: CartFacadeActions['updateProduct'] = (
      { count, price_id, product_id },
      moeTrack
    ) => {
      const uuid = getLocalCartUuid(appMode, storeId);
      const cart = uuid ? cartSnapshot(uuid) : null;
      if (!uuid || !cart) return;

      const cartProduct = cart.products.find(
        (product) =>
          product.id === product_id || product.product_id === product_id
      );
      const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

      mutateUpdateCartProduct(
        {
          appMode,
          cartUuid: uuid,
          count,
          jwt: getJwt(),
          priceId: price_id,
          productId: Number(product_id),
        },
        {
          onSuccess: () => {
            if (changeInCount <= 0 || !cartProduct) return;
            moeTrack(
              moeAddToBag({
                cartUuid: uuid,
                product: { ...cartProduct, count: changeInCount },
                storeId: store?.id ?? null,
                storeName: store?.name ?? null,
              })
            );
          },
        }
      );
    };

    return {
      ...v1Actions,

      updateProduct,

      // `attemptToAddToCart`'s three client concerns, in its order — the guard
      // returns before any analytics fire.
      addProduct: ({
        almostGone,
        columnPosition,
        count,
        location,
        menuProduct,
        moeTrack,
        navigate,
        price_id,
        productLocation,
        rowPosition,
        source,
        special,
        store: addStore,
        userPreferences,
        userSegments,
        withoutDrawer = false,
      }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        // Unlike the other actions, a missing uuid or cart is not a no-op here —
        // it is the create path. Absent both, no cart store can conflict and the
        // branch below is always "add".
        const cart = uuid ? cartSnapshot(uuid) : null;
        const storeAndProduct = prepareCartProduct(
          addStore,
          menuProduct,
          price_id,
          count
        );

        // §3 exception, still Redux: `maybeChangeStore` parks the attempted
        // product in the slice's `replacementCart`, which `/replaceCart` reads
        // back on another route. The hand-off is Redux, not a callback.
        if (cart && cart.store.id !== addStore.id) {
          return maybeChangeStore(appMode, dispatch, storeAndProduct, navigate);
        }

        // v1's `isProductInCart` matches on `id` alone. Kept strict so the
        // flag-on add-vs-update choice is the choice v1 made; `updateProduct`'s
        // looser finder is not this slot's to change.
        const productInCart = cart?.products.some(
          (product) =>
            product.id === menuProduct.id && product.price_id === price_id
        );

        if (productInCart) {
          trackCartEvent({
            count,
            event: EventNames.UpdatedProductInCart,
            location,
            menuProduct,
            price_id,
            productLocation,
            source,
            special,
            store: addStore,
            userPreferences,
            userSegments,
          });
          updateProduct(
            { count, price_id, product_id: menuProduct.id },
            moeTrack
          );
          return;
        }

        const basePrice = Number(menuProduct[`price_${price_id}`]);
        const discountedPrice = Number(
          menuProduct[`discounted_price_${price_id}`]
        );
        postAnalyticsEvent({
          name: 'cartItemAdd',
          properties: {
            product: {
              price_id,
              ...pick(menuProduct, [
                'product_id',
                'name',
                'brand',
                'category',
                'kind',
              ]),
            },
            productId: menuProduct.product_id || menuProduct.id,
            quantity: count,
            ...(basePrice > 0 && { price: basePrice }),
            ...(discountedPrice > 0 && { discountedPrice }),
          },
        });

        trackCartEvent({
          almostGone,
          cartId: cart?.id,
          columnPosition,
          count,
          event: EventNames.AddedProductToCart,
          location,
          menuProduct,
          price_id,
          productLocation,
          rowPosition,
          source,
          special,
          store: addStore,
          userPreferences,
          userSegments,
        });

        const onAdded = (cartUuid: string) => {
          moeTrack(
            moeAddToBag({
              cartUuid,
              product: storeAndProduct.product,
              storeId: addStore.id,
              storeName: addStore.name,
            })
          );

          // §3 exception, still Redux: `openCart` sets `cart.is_open`, client UI
          // state the v3 payload does not carry (the mapper drops it).
          if (appMode !== 'headless' && appMode !== 'bloom' && !withoutDrawer) {
            dispatch(openCart());
          }
        };

        // `product_id` on the wire is `menuProduct.id`, as v1 sent
        // (`sources/cartProducts.ts:40`). The menu-card and cart-topper shapes
        // `omit(mp, 'product_id')` after copying it onto `id`, so sending
        // `menuProduct.product_id` would put `undefined` on the wire for every
        // grid add.
        if (!uuid) {
          // `create_params` omits `recommended`; not a divergence — v1's add
          // dropped it too (`Carts::AddProduct` never assigns the column).
          return createV3Cart(
            [{ count, price_id, product_id: menuProduct.id }],
            addStore.id,
            (created) => onAdded(created.uuid)
          );
        }

        mutateAddCartProduct(
          {
            appMode,
            cartUuid: uuid,
            deviceId: janeDeviceId,
            jwt: getJwt(),
            product: {
              count,
              price_id,
              product_id: menuProduct.id,
              // Inert: permitted, but `Carts::AddProduct` never assigns it.
              // Sent for byte-parity with v1's add payload.
              recommended: !!menuProduct.recommended,
            },
            storeId: addStore.id,
            tags: cartTags,
          },
          { onSuccess: (data) => onAdded(data.cart_uuid) }
        );
      },

      // Guarded on the uuid: a second create would orphan the live cart, and
      // v1's no-op could never do that.
      createCart: () => {
        if (getLocalCartUuid(appMode, storeId) || !store) return;
        createV3Cart([], store.id);
      },

      removeProduct: (productId, priceId) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        const cart = uuid ? cartSnapshot(uuid) : null;
        if (!uuid || !cart) return;

        const product = cart.products.find(
          (candidate) =>
            candidate.id === productId && candidate.price_id === priceId
        );
        // v1's predicate, kept for `cartEmptied` parity (`cart.ts:351`). Not a
        // destroy predictor — see `settleAfterRemoval`.
        const wasOnlyLine = cart.products.length === 1;

        if (product) {
          track({
            event: EventNames.RemovedProductFromCart,
            productBrand: product.brand,
            productCategory: product.category,
            productId: productId.toString(),
            productName: product.name,
            // Deliberately no special — the base `price_${priceId}`, as v1 reads.
            productPrice: getActualUnitPrice(product, product.price_id) || 0,
            quantity: product.count,
            storeCurrency: store?.currency_code,
          });
        }

        const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
        const discountedPrice = product
          ? Number(product[`discounted_price_${priceId}`])
          : 0;

        mutateRemoveCartProduct(
          {
            appMode,
            cartUuid: uuid,
            jwt: getJwt(),
            priceId,
            productId: Number(productId),
          },
          {
            onSuccess: () => {
              postAnalyticsEvent({
                name: 'cartItemRemoval',
                properties: {
                  priceId,
                  productId,
                  ...(basePrice > 0 && { price: basePrice }),
                  ...(discountedPrice > 0 && { discountedPrice }),
                },
              });

              if (wasOnlyLine) postAnalyticsEvent({ name: 'cartEmptied' });

              settleAfterRemoval(uuid);
            },
          }
        );
      },

      updateReservationMode: ({ reservationMode, userGroupSpecialId }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Not dual-write: the write and the read are both v3. A controlled
        // toggle cannot wait on the round-trip, so patch the cache and roll the
        // snapshot back if the PATCH rejects.
        const queryKey = cartKeys.detail(uuid);
        // An in-flight refetch would resolve with the pre-toggle mode and clobber
        // the patch below — likelier now that the cart is never fresh. Not
        // awaited: the abort is synchronous, and the toggle is controlled, so
        // deferring the patch past the click would drop a frame.
        queryClient.cancelQueries({ queryKey });
        const previousCart = queryClient.getQueryData<Cart>(queryKey);
        if (previousCart) {
          queryClient.setQueryData<Cart>(queryKey, {
            ...previousCart,
            reservation_mode: reservationMode,
          });
        }

        mutateUpdateCart(
          {
            appMode,
            jwt: getJwt(),
            reservationMode,
            userGroupSpecialId,
            uuid,
          },
          {
            onError: () => {
              // The snapshot restores immediately, but under a double-toggle it
              // is itself an unconfirmed optimistic value, so reconcile with the
              // server rather than trusting it. Success needs no invalidate —
              // `useUpdateCart` writes the response cart.
              if (previousCart) {
                queryClient.setQueryData(queryKey, previousCart);
              }
              queryClient.invalidateQueries({ queryKey });
            },
          }
        );
      },

      applyLoyalty: (crmParams) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Only the nine keys the v3 controller permits; the rest of the v1
        // redemption object is dropped by the endpoint's own contract.
        mutateAddLoyalty(
          {
            appMode,
            cartUuid: uuid,
            crmMemberPoints: crmParams.crm_member_points,
            crmProvider: crmParams.crm_provider,
            crmRewardId: String(crmParams.crm_reward_id),
            jwt: getJwt(),
            pointValue: crmParams.point_value,
            redemptionChecksum: crmParams.redemption_checksum,
            redemptionUrl: crmParams.redemption_url,
            reward: crmParams.reward,
            rewardName: crmParams.reward_name,
          },
          {
            onError: (error) => {
              const rejected =
                error instanceof ApiError && error.response.status === 422;

              NotificationsService.error(
                rejected ? error.message : CRM_REDEMPTION_ERROR_MESSAGE
              );
              trackError(error, {
                cartUuid: uuid,
                crmProvider: crmParams.crm_provider,
                crmRewardId: crmParams.crm_reward_id,
              });
            },
          }
        );
      },

      // `crmProvider` is unused on v3 — `destroy` finds the redemption by id alone.
      removeLoyalty: (id) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateRemoveLoyalty({
          appMode,
          cartUuid: uuid,
          jwt: getJwt(),
          redemptionId: Number(id),
        });
      },

      deleteUnavailableProducts: (products) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid || !products.length) return;

        // The caller's rows are authoritative, not a cache snapshot: both paths
        // into this modal refresh the v1 slice via a thunk that never invalidates
        // `['cart', uuid]`, so a snapshot can still report none unavailable.
        const jwt = getJwt();

        // One terminal cache write, not one per delete — which is why this uses
        // the raw API fn rather than `useRemoveCartProduct` (that hook
        // invalidates on every success). `allSettled` so one rejection does not
        // strand the rest.
        Promise.allSettled(
          products.map((product) =>
            removeCartProduct({
              appMode,
              cartUuid: uuid,
              jwt,
              priceId: product.price_id,
              productId: product.product_id,
            })
          )
        ).then((results) => {
          // v1 cleared every unavailable row in one atomic request, so a partial
          // failure is new here. The refetch below re-raises the alert with
          // whatever survived, but nothing else would record why.
          const rejected = results.filter(
            (result) => result.status === 'rejected'
          );
          if (rejected.length) {
            trackError(
              new Error('Failed to delete unavailable cart products'),
              {
                cartUuid: uuid,
                failed: rejected.length,
                reasons: rejected.map((result) => String(result.reason)),
                requested: products.length,
              }
            );
          }

          settleAfterRemoval(uuid);
        });
      },

      resetCart: () => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateDeleteCart(
          { appMode, jwt: getJwt(), uuid },
          { onSuccess: () => updateLocalCartUuid(appMode, storeId, null) }
        );
      },
    };
  }, [
    appMode,
    cartTags,
    dispatch,
    janeDeviceId,
    mutateAddCartProduct,
    mutateAddLoyalty,
    mutateCreateCart,
    mutateDeleteCart,
    mutateRemoveCartProduct,
    mutateRemoveLoyalty,
    mutateUpdateCart,
    mutateUpdateCartProduct,
    queryClient,
    store,
    storeId,
    useV3Cart,
  ]);
};
